// ---------------------------------------------------------------------------
// ResponsiblesPane — aba "Responsáveis" do InputDrawerV3.
// ---------------------------------------------------------------------------
// Mostra a lista de pessoas responsaveis pelo input (originalmente vivia
// como uma linha "Responsavel" dentro de "Sobre este input"; foi
// promovida a aba propria por dois motivos: (1) e *editavel*, ao
// contrario das demais propriedades que sao snapshot-only; (2) o
// usuario explicitamente pediu uma secao dedicada.
//
// Permissionamento (rev. 2026-04-29):
//   Edicao restrita aos papeis ``DEV``, ``IMG`` (integration manager)
//   e ``CAD`` (client admin) — mesma matriz usada pelo
//   ``InputDrawerDetailsPanel`` da V2 (linhas 160/180). Demais
//   papeis (consultor, viewer) caem em modo somente-leitura: chips
//   exibidos sem botao de editar.
//
//   Reaproveita o endpoint ``api/inputs_save_responsible/`` da V2
//   (mesmo modelo ``Input``, mesmas permissoes no backend). Assim
//   evitamos uma nova rota e uma nova migration so para o V3 — o
//   contrato dos responsaveis nao mudou entre as versoes.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ThreeDots } from "react-loader-spinner";
import ReactSelect from "react-select";
import FormControl from "@material-ui/core/FormControl";
import { useTranslation } from "react-i18next";

import PersonOutline from "@material-ui/icons/PersonOutline";

import useDrawerStyles from "../styles";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../../styles";
import { whiteColor } from "../../../../../assets/jss/material-dashboard-pro-react";

// Papeis com permissao para EDITAR a lista de responsaveis. Espelha
// o que o backend ja autoriza no endpoint
// ``api/inputs_save_responsible/`` e a UX da V2.
const EDITOR_ROLES = ["DEV", "IMG", "CAD"];

// Compara duas listas de responsaveis (cada item ``{id|email,name}``)
// pelo conjunto de IDs. Retorna ``true`` se sao iguais (mesmos
// responsaveis, ordem irrelevante). Usado para desabilitar o botao
// "Salvar" quando nao houve mudanca em relacao ao snapshot do input.
const sameResponsibles = (a, b) => {
  const sa = new Set((a || []).map((r) => r?.id || r?.email || r?.name));
  const sb = new Set((b || []).map((r) => r?.id || r?.email || r?.name));
  if (sa.size !== sb.size) return false;
  for (const v of sa) if (!sb.has(v)) return false;
  return true;
};

const ResponsiblesPane = ({
  input,
  usersList,
  userRole,
  onSaveResponsibles,
  isSavingResponsibles,
  selectStyles,
}) => {
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();

  const canEdit = EDITOR_ROLES.includes(userRole);
  const initial = useMemo(
    () => (Array.isArray(input?.responsible) ? input.responsible : []),
    [input?.responsible],
  );

  // Assinatura por VALOR da lista salva (ids ordenados). Usada como
  // dependencia do resync no lugar da referencia do array: o polling do
  // snapshot no parent recria ``input.responsible`` (nova referencia,
  // mesmo conteudo) a cada tick — depender da referencia disparava o
  // ``setDraft(initial)`` e DESCARTAVA a selecao em curso ainda nao
  // salva (bug: ao escolher o 2o responsavel, o 1o sumia). Com a
  // assinatura, um refresh que devolve os mesmos responsaveis nao
  // reseta; so um salvamento real (conteudo diferente) ou a troca de
  // input ressincroniza.
  const initialSig = useMemo(
    () =>
      (initial || [])
        .map((r) => r?.id || r?.email || r?.name)
        .sort()
        .join("|"),
    [initial],
  );

  // Estado local da selecao em curso. So existe quando ``canEdit`` —
  // viewers nao tocam nesse path. Resincronizado quando a identidade do
  // input muda OU quando o backend confirma uma gravacao (a assinatura
  // ``initialSig`` muda de valor).
  const [draft, setDraft] = useState(initial);
  useEffect(() => {
    // ``initial`` e' lido no momento do fire (referencia corrente); a
    // dependencia e' a assinatura por valor (``initialSig``) + identidade
    // do input — assim NAO resetamos a cada nova referencia de
    // ``input.responsible`` vinda do polling do snapshot.
    setDraft(initial);
  }, [initialSig, input?.input_id, input?.meta_name]);

  // Lista de opcoes do ReactSelect — todos os usuarios da
  // implementacao. Filtramos defensivamente para evitar entradas
  // sem id/name (corrompem o "value").
  const options = useMemo(() => {
    if (!Array.isArray(usersList)) return [];
    return usersList
      .filter((u) => u && (u.id || u.email) && u.name)
      .map((u) => ({ value: u.id || u.email, label: u.name }));
  }, [usersList]);

  const selectedValue = draft.map((r) => ({
    value: r.id || r.email || r.name,
    label:
      r.name ||
      r.email ||
      t("modeling_inputs_v3.responsibles_pane.user_fallback", {
        id: r.id || "",
      }),
  }));

  const handleChange = (selected) => {
    const next = (selected || []).map((opt) => ({
      id: opt.value,
      name: opt.label,
    }));
    setDraft(next);
  };

  const handleSave = () => {
    if (isSavingResponsibles) return;
    if (!onSaveResponsibles) return;
    onSaveResponsibles(draft);
  };

  const isDirty = !sameResponsibles(initial, draft);

  return (
    <div
      ref={wheelRef}
      className={`${classes.paneScroll} ${overlayScrollClasses.overlayScroll}`}
    >
      <div className={classes.section}>
        <div className={classes.sectionTitle}>
          {t("modeling_inputs_v3.responsibles_pane.section_title")}
          <span className={classes.sectionCount}>{initial.length}</span>
        </div>

        <div className={classes.muted} style={{ marginBottom: 22 }}>
          {t("modeling_inputs_v3.responsibles_pane.description")}
        </div>

        {/* Modo somente-leitura: usuarios fora de DEV/IMG/CAD veem
            apenas a lista atual em chips, sem editor. */}
        {!canEdit && (
          <>
            {initial.length === 0 ? (
              <div
                className={classes.muted}
                style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
              >
                <PersonOutline style={{ flex: "0 0 auto", marginTop: 2 }} />
                <span>
                  {t("modeling_inputs_v3.responsibles_pane.no_responsibles")}
                </span>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                {initial.map((r) => (
                  <span
                    key={r.id || r.email || r.name}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "5px 12px",
                      borderRadius: 999,
                      background: "#f5f5f5",
                      color: "#444",
                      fontSize: 12,
                      border: "1px solid #eee",
                    }}
                  >
                    <PersonOutline style={{ fontSize: 14, opacity: 0.7 }} />
                    {r.name ||
                      r.email ||
                      t("modeling_inputs_v3.responsibles_pane.user_fallback", {
                        id: r.id || "",
                      })}
                  </span>
                ))}
              </div>
            )}
            <div
              className={classes.muted}
              style={{ marginTop: 10, fontSize: 11 }}
            >
              {t("modeling_inputs_v3.responsibles_pane.read_only_note")}
            </div>
          </>
        )}

        {/* Modo edicao: ReactSelect multi + botao Salvar. Mesmo
            visual e mesmo endpoint do drawer V2 — replicar UX
            evita o consultor reaprender o fluxo. */}
        {canEdit && (
          <>
            <FormControl fullWidth>
              <ReactSelect
                isMulti
                isClearable={false}
                closeMenuOnSelect={false}
                options={options}
                value={selectedValue}
                onChange={handleChange}
                placeholder={t(
                  "modeling_inputs_v3.responsibles_pane.select_placeholder",
                )}
                styles={{
                  ...(selectStyles || {}),
                  // ``menuPortal`` precisa subir o z-index para nao
                  // ficar atras do drawer (z-index padrao do MUI ~1300).
                  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                }}
                // Render do menu em portal: tira o popup do
                // container ``useNativeWheelScroll``, que estava
                // sequestrando o wheel e rolando o pane atras em vez
                // do menu — sintoma reportado pelo consultor.
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                isDisabled={isSavingResponsibles}
              />
            </FormControl>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 14,
              }}
            >
              <button
                type="button"
                className={`${classes.xlinkBtn} ${classes.xlinkBtnPrimary}`}
                onClick={handleSave}
                disabled={!isDirty || isSavingResponsibles}
                title={
                  !isDirty
                    ? t(
                        "modeling_inputs_v3.responsibles_pane.save_btn_no_changes",
                      )
                    : isSavingResponsibles
                      ? t(
                          "modeling_inputs_v3.responsibles_pane.save_btn_saving",
                        )
                      : undefined
                }
              >
                {isSavingResponsibles ? (
                  <ThreeDots color={whiteColor} height={10} width={32} />
                ) : (
                  t("modeling_inputs_v3.responsibles_pane.save_btn")
                )}
              </button>
              {isDirty && !isSavingResponsibles && (
                <button
                  type="button"
                  className={`${classes.xlinkBtn} ${classes.xlinkBtnGhost}`}
                  onClick={() => setDraft(initial)}
                >
                  {t("modeling_inputs_v3.responsibles_pane.discard_btn")}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ResponsiblesPane.propTypes = {
  input: PropTypes.object,
  // Lista canonica de usuarios da implementacao. Cada item tem
  // ``{id, name, email?}``. Quando vazia, o editor desabilita
  // (nao ha quem selecionar).
  usersList: PropTypes.array,
  // Papel do usuario atual (``DEV`` / ``IMG`` / ``CAD`` / ``CON`` /
  // ``CSU``). So habilitamos edicao para os tres primeiros.
  userRole: PropTypes.string,
  // Callback ``(novaListaDeResponsaveis) => void`` — o parent dispara
  // a chamada HTTP e atualiza ``input.responsible`` no sucesso.
  onSaveResponsibles: PropTypes.func,
  // Indicador de "salvamento em curso" — desabilita o select e o
  // botao enquanto o request esta no ar.
  isSavingResponsibles: PropTypes.bool,
  // Estilos do ReactSelect (mesmo objeto usado em outras partes do
  // V3 — passado pelo parent para nao redefinir aqui).
  selectStyles: PropTypes.object,
};

export default ResponsiblesPane;
